import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, Bot, Check, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/common";
import { PageError, PageLoading } from "@/components/LiveState";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { buildWorkspaceOptions } from "@/components/workspaceOptions";
import { MAX_BRANDS_PER_READ, brandRow, brandsHeadline, sortByAttention, spentLabel } from "@/components/brands/brandsView";

// All brands: each of the person's brand agents on one line, with what it needs from them. Opening a line switches the
// workspace, the way the sidebar's switcher does, and lands on that brand's page; a fix link lands on the page that
// fixes it. What each line says comes from components/brands/brandsView.js; this file only loads, lays out and opens.

// The brand panel's navy, the Alloy card's.
const HERO = "radial-gradient(360px 220px at 90% 0%, rgba(255,255,255,.12), transparent 70%), linear-gradient(135deg, #0F2442 0%, #003B8D 58%, #2F6DB5 100%)";
const PILL = {
  ok: "bg-[#22C55E]/20 text-[#86EFAC]",
  warn: "bg-[#F59E0B]/25 text-[#FCD34D]",
  bad: "bg-[#EF4343]/25 text-[#FECACA]",
};
const DOT = { ok: "bg-[#22C55E]", warn: "bg-[#F59E0B]", bad: "bg-[#EF4343]" };
const FIGURE_TONE = { ok: "text-sm-ink", warn: "text-[#B45309]", bad: "text-[#B91C1C]" };
const CHIP = {
  ok: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#15803D]",
  warn: "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#B45309]",
  bad: "border-[#EF4343]/30 bg-[#EF4343]/[0.08] text-[#B91C1C]",
};
const PAGE_PATHS = { connections: "/connections", alloy: "/alloy" };

function plural(count, singular, pluralForm = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** What a fix link does, for a reader who hears it without the line it sits in: "Reconnect Shopify for Halden Pets". */
function actionAria(item, clientName) {
  const subject = item.text.replace(/ (is not working|has not been tested)$/, "");
  if (item.action.label === "Reconnect") return `Reconnect ${subject} for ${clientName}`;
  if (item.action.label === "Test it") return `Test ${subject} for ${clientName}`;
  return `${item.action.label} for ${clientName}`;
}

function Figure({ figure }) {
  const tone = FIGURE_TONE[figure.tone] || FIGURE_TONE.ok;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-sm-ink/70">{figure.label}</dt>
      <dd className={`m-0 text-sm font-bold tabular-nums ${tone}`}>
        {figure.value}
        <span className={`block text-xs font-normal ${figure.tone === "ok" ? "text-sm-ink/70" : ""}`}>{figure.sub}</span>
      </dd>
    </div>
  );
}

function BrandLine({ row, headingId, busy, disabled, onOpen }) {
  const pillTone = PILL[row.pill.tone] || PILL.ok;
  return (
    <article
      data-testid={`brand-row-${row.clientSlug}`}
      aria-labelledby={headingId}
      aria-busy={busy || undefined}
      className="relative grid cursor-pointer overflow-hidden rounded-2xl border border-sm-ink/10 bg-white transition-[border-color,box-shadow,transform] hover:border-sm-accent/60 hover:shadow-[0_18px_36px_-24px_rgba(0,59,141,0.5)] motion-safe:hover:-translate-y-px md:grid-cols-[280px_minmax(0,1fr)_auto]"
    >
      <div className="flex items-center gap-3 px-[18px] py-4 text-white" style={{ background: HERO }}>
        <span aria-hidden="true" className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#9CC6F0] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2)]">
          <Bot size={20} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5 font-display text-[22px] font-extrabold leading-tight">
            <span>{row.agentName}</span>
            <span data-testid={`brand-state-${row.clientSlug}`} className={`inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-extrabold uppercase tracking-[0.12em] ${pillTone}`}>
              <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${DOT[row.pill.tone] || DOT.ok}`} />
              {row.pill.label}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-white/85">
            <h2 id={headingId} className="m-0 inline text-xs font-normal">
              {/* The whole line opens the brand: this is its one control, stretched over the line; the fix links sit above it. */}
              <button
                type="button"
                aria-label={`Open ${row.clientName}`}
                disabled={disabled}
                onClick={() => onOpen(row, "/")}
                className="font-semibold text-white after:absolute after:inset-0 after:rounded-2xl after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-[3px] focus-visible:after:-outline-offset-[3px] focus-visible:after:outline-sm-accent disabled:cursor-progress"
              >
                {row.clientName}
              </button>
            </h2>
            {` · ${row.roleLabel}`}
            {row.here ? " · You're here" : ""}
          </div>
        </div>
      </div>
      <div className="flex min-w-0 flex-col justify-center gap-2.5 px-5 py-3.5">
        {row.items.length === 0 ? (
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#15803D]">
            <Check size={14} aria-hidden="true" /> Nothing needs fixing
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1.5 p-0">
            {row.items.map((item) => (
              <li key={item.text} className="flex items-baseline gap-2 text-[13px] font-bold text-sm-ink">
                <span aria-hidden="true" className={`inline-block h-[7px] w-[7px] shrink-0 -translate-y-px rounded-full ${DOT[item.tone] || DOT.warn}`} />
                <span>
                  {item.text}
                  {item.action ? (
                    <button
                      type="button"
                      aria-label={actionAria(item, row.clientName)}
                      disabled={disabled}
                      onClick={() => onOpen(row, PAGE_PATHS[item.action.page] || "/")}
                      className="relative z-10 ml-1.5 whitespace-nowrap text-xs font-bold text-sm-primary underline decoration-sm-primary/35 underline-offset-2 hover:text-sm-ink hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-progress"
                    >
                      {item.action.label}
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
            {row.more > 0 ? <li className="text-[13px] font-bold text-sm-ink/70">{`+${row.more} more`}</li> : null}
          </ul>
        )}
        <dl className="m-0 grid grid-cols-3 gap-x-6 gap-y-2.5">
          <Figure figure={row.figures.credits} />
          <Figure figure={row.figures.connections} />
          <Figure figure={row.figures.month} />
        </dl>
      </div>
      <span aria-hidden="true" className="hidden w-14 items-center justify-center border-l border-sm-ink/10 text-sm-primary md:flex">
        <ChevronRight size={20} />
      </span>
    </article>
  );
}

export default function AllBrands() {
  const { memberships, activeClient, selectClient } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const options = useMemo(() => buildWorkspaceOptions(memberships, activeClient), [memberships, activeClient]);
  const brandOptions = useMemo(() => options.filter((option) => option.kind === "brand"), [options]);
  const slugs = useMemo(() => brandOptions.slice(0, MAX_BRANDS_PER_READ).map((option) => option.clientSlug), [brandOptions]);
  const hiddenTests = options.length - brandOptions.length;
  const unread = brandOptions.length - slugs.length;
  const resource = useLiveResource("getBrands", [slugs], { refreshMs: 120_000, dedupe: true });
  const [opening, setOpening] = useState(null);
  const openError = location.state?.openError ?? null;

  const rows = useMemo(() => {
    const bySlug = new Map((resource.data?.brands ?? []).map((brand) => [brand.clientSlug, brand]));
    return slugs.map((slug) => bySlug.get(slug)).filter(Boolean).map((brand) => brandRow(brand, { activeSlug: activeClient?.clientSlug }));
  }, [resource.data, slugs, activeClient?.clientSlug]);
  const headline = useMemo(() => brandsHeadline(rows), [rows]);

  // The page leaves first, then the workspace changes, so the brand's page never loads under the old workspace twice.
  // A switch that fails comes back here with the brand's name.
  const open = (row, path) => {
    if (opening) return;
    if (!activeClient || row.clientSlug === activeClient.clientSlug) {
      navigate(path);
      return;
    }
    setOpening(row.clientSlug);
    navigate(path);
    Promise.resolve()
      .then(() => selectClient(row.clientSlug))
      .catch(() => navigate("/brands", { replace: true, state: { openError: row.clientName } }));
  };

  if (resource.status === "loading" && !resource.data) return <PageLoading label="Reading your brands…" />;
  if (resource.status === "error") return <PageError error={resource.error} onRetry={resource.reload} title="Your brands are unavailable" />;
  return <AllBrandsView rows={rows} headline={headline} hiddenTests={hiddenTests} unread={unread} openError={openError} opening={opening} onOpen={open} />;
}

/** The page once its brands are read. The dev fixture (/__dev/brands) draws it from made-up rows. */
export function AllBrandsView({ rows, headline, hiddenTests = 0, unread = 0, openError = null, opening = null, onOpen }) {
  // The attention chip is a switch: pressed, the lines that need a person come first; pressed again, the list is as before.
  const [attentionFirst, setAttentionFirst] = useState(false);
  const chipTone = headline.attention === 0 ? "ok" : rows.some((row) => row.tone === "bad") ? "bad" : "warn";
  const chipClass = `inline-flex h-8 items-center gap-2 rounded-full border px-3.5 text-[13px] font-bold ${CHIP[chipTone]}`;
  const shownRows = attentionFirst && headline.attention > 0 ? sortByAttention(rows) : rows;
  return (
    <div data-testid="all-brands-page">
      <PageHeader title="All brands" subtitle="Each of your agents on one line, with what it needs from you." testId="all-brands-header">
        {headline.attention === 0 ? (
          <span data-testid="brands-attention" className={chipClass}>
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT[chipTone]}`} />
            Nothing needs attention
          </span>
        ) : (
          <button
            type="button"
            data-testid="brands-attention"
            aria-pressed={attentionFirst}
            onClick={() => setAttentionFirst((value) => !value)}
            className={`${chipClass} cursor-pointer transition-[box-shadow,background-color] hover:shadow-[0_0_0_3px_rgba(15,36,66,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent focus-visible:ring-offset-2 ${attentionFirst ? "ring-1 ring-inset ring-current" : ""}`}
          >
            {attentionFirst ? <ArrowUp size={13} aria-hidden="true" /> : <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT[chipTone]}`} />}
            {`${headline.attention} of ${headline.total} need attention`}
            {/* Pressed, the chip says so in words; unpressed, only a reader hears what pressing does. */}
            {attentionFirst ? <span className="font-normal"> · shown first</span> : <span className="sr-only">, show them first</span>}
          </button>
        )}
        <span data-testid="brands-spend" className="text-[13px] text-sm-ink/80">
          Spent this month: <strong className="tabular-nums">{spentLabel(headline)}</strong>
          {headline.spentComplete ? "" : " (not every brand could be read)"}
        </span>
      </PageHeader>

      {openError ? (
        <div role="alert" className="mb-4 rounded-xl border border-[#EF4343]/30 bg-[#EF4343]/[0.08] px-4 py-3 text-sm text-[#B91C1C]">
          {`${openError} could not be opened. Try again, or pick it from the sidebar.`}
        </div>
      ) : null}

      <p className="mb-2.5 text-xs text-sm-ink/70">Click a brand to open it.</p>
      <div className="flex flex-col gap-3">
        {shownRows.map((row) => (
          <BrandLine
            key={row.clientSlug}
            row={row}
            headingId={`brand-${row.clientSlug}`}
            busy={opening === row.clientSlug}
            disabled={Boolean(opening)}
            onOpen={onOpen}
          />
        ))}
      </div>

      {hiddenTests > 0 || unread > 0 ? (
        <p data-testid="brands-hidden-note" className="mt-4 text-xs text-sm-ink/70">
          {[
            hiddenTests > 0 ? `${plural(hiddenTests, "test & sandbox workspace")} ${hiddenTests === 1 ? "is" : "are"} not listed here.` : "",
            unread > 0 ? `${plural(unread, "more brand")} ${unread === 1 ? "is" : "are"} in the sidebar's switcher.` : "",
          ].filter(Boolean).join(" ")}
        </p>
      ) : null}
    </div>
  );
}
